import React, { useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  AccountData,
  AreaOccupancyData,
  OrderData,
  SchedulerConfigOccupancyData,
  SchedulerConfigurationData,
  UnavailabilityData,
  UnavailabilityService,
} from '../../../api';
import { formatSeconds } from '../utils/areasFormat';
import { useSafeExecute } from '../../../common/apiNotifications/safeExecute';
import { useCalendar } from '../../CalendarContext/CalendarContext';
import { isSimpleAreaBlock } from '../utils/areaBlocks';
import { freeBitsAt, pickPartialBlockBits } from '../utils/occupancyMath';
import { indexesToMask, localizedLabel } from '../../../utils/maskUtils';
import { areaUsesTicketWording, configDayBounds, primaryPageForArea } from '../utils/workingHours';
import { AREAS_PALETTE } from '../areasPalette';
import SharedMobileTimePicker from '../../../common/ui/SharedMobileTimePicker';

export type BlockAreaTarget = {
  area: AreaOccupancyData;
  occupancyConfig: SchedulerConfigOccupancyData;
  config: SchedulerConfigurationData | undefined;
};

export enum BlockScope {
  WHOLE_AREA = 'whole_area',
  PARTIAL = 'partial',
}

export enum BlockWhen {
  ALL_DAY = 'all_day',
  TIME_RANGE = 'time_range',
}

type BlockAreaDialogProps = {
  target: BlockAreaTarget;
  account: AccountData;
  currentDate: string;
  selectedSeconds: number;
  dayUnavailabilities: UnavailabilityData[];
  /** The config's orders, joined from the orders cache by `order_ids`. */
  orders: OrderData[];
  /** Blocks already recorded against this area today, listed so they can be lifted here. */
  areaBlocks: UnavailabilityData[];
  timeFormat: string;
  language: string;
  onClose: () => void;
  onBlocked: () => Promise<void>;
};

/**
 * Two independent axes — what to close (all of it / N tables) and when (all day / a range) — freely
 * combinable, because "four tables from 14:00 to 18:00" is a real thing staff need to say.
 *
 * A partial block takes the tables that are FREE at the selected time: blocking an occupied table
 * would not reduce what is still sellable.
 */
export const BlockAreaDialog = ({
  target,
  account,
  currentDate,
  selectedSeconds,
  dayUnavailabilities,
  orders,
  areaBlocks,
  timeFormat,
  language,
  onClose,
  onBlocked,
}: BlockAreaDialogProps) => {
  const { t, i18n } = useTranslation();
  const safeExecute = useSafeExecute();
  const { openExperienceAvailabilityDrawer } = useCalendar();
  const { area, occupancyConfig, config } = target;

  const areaLabel = localizedLabel(area.label, language, area.area_id);
  const configLabel = localizedLabel(occupancyConfig.label, language);
  const swatchColor = primaryPageForArea(area, config, account)?.color || AREAS_PALETTE.defaultPageColor;
  const unitWord = areaUsesTicketWording(area, config)
    ? t('calendar.areas_view.unit.stations')
    : t('calendar.areas_view.unit.tables');
  const slotMinutes = occupancyConfig.slot_minutes;

  const freeBits = useMemo(
    () => (config ? freeBitsAt(area, occupancyConfig, config, orders, dayUnavailabilities, selectedSeconds) : []),
    [area, occupancyConfig, config, orders, dayUnavailabilities, selectedSeconds],
  );

  // The picker is bounded by the config's working hours and stepped to its slot size, so a block
  // can only ever start and end on a real scheduler slot.
  const bounds = config ? configDayBounds(config, account, currentDate) : null;
  const dayStartSeconds = bounds?.startSeconds ?? 0;
  const dayEndSeconds = bounds?.endSeconds ?? 24 * 3600;
  const asTime = (seconds: number) => moment.unix(seconds).tz('utc');
  const secondsOf = (value: Moment | null) => (value ? value.hours() * 3600 + value.minutes() * 60 : 0);

  const [scope, setScope] = useState<BlockScope>(BlockScope.WHOLE_AREA);
  const [when, setWhen] = useState<BlockWhen>(BlockWhen.ALL_DAY);
  const [tableCount, setTableCount] = useState(1);
  // Default to "from the selected time until closing", aligned to the slot grid and kept inside
  // the working day — the selected time can sit outside it in live mode late at night.
  const slotSeconds = slotMinutes * 60;
  const [fromSeconds, setFromSeconds] = useState(() => {
    const aligned = Math.floor(selectedSeconds / slotSeconds) * slotSeconds;
    return Math.min(Math.max(aligned, dayStartSeconds), Math.max(dayEndSeconds - slotSeconds, dayStartSeconds));
  });
  const [toSeconds, setToSeconds] = useState(dayEndSeconds);
  const [description, setDescription] = useState<string>(() =>
    t('calendar.areas_view.block_description_default', { area: areaLabel }),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requestedCount = Math.max(1, Math.min(tableCount, area.total));
  const blockedBits = scope === BlockScope.WHOLE_AREA ? null : pickPartialBlockBits(freeBits, requestedCount);
  const willBeCapped = blockedBits !== null && blockedBits.length < requestedCount;
  const isRangeInvalid = when === BlockWhen.TIME_RANGE && toSeconds <= fromSeconds;
  const hasNothingToBlock = blockedBits !== null && blockedBits.length === 0;

  const removeBlock = async (block: UnavailabilityData) => {
    if (!isSimpleAreaBlock(block, area)) {
      // Repeating or partial blocks keep their full shape in the unavailability drawer.
      openExperienceAvailabilityDrawer({ unavailability: block });
      onClose();
      return;
    }
    try {
      await safeExecute({
        apiCall: () => UnavailabilityService.deleteUnavailability(block.id),
        successMessage: t('calendar.areas_view.area_opened', { area: areaLabel }),
      });
      await onBlocked();
    } catch {
      // safeExecute surfaced the failure; leave the dialog open so the admin can retry.
    }
  };

  const blockTimeLabel = (block: UnavailabilityData) =>
    block.all_day
      ? t('calendar.areas_view.all_day')
      : `${formatSeconds(block.start_time, timeFormat)}–${formatSeconds(block.end_time, timeFormat)}`;

  const submit = async () => {
    const blockedMask = blockedBits === null ? area.mask : indexesToMask(blockedBits, occupancyConfig.resource_count);
    const isAllDay = when === BlockWhen.ALL_DAY;

    setIsSubmitting(true);
    try {
      await safeExecute({
        apiCall: () =>
          UnavailabilityService.createUnavailability({
            all_day: isAllDay,
            description,
            start_date: currentDate,
            end_date: currentDate,
            start_time: isAllDay ? 0 : fromSeconds,
            end_time: isAllDay ? 0 : toSeconds,
            shared_resources_blocks: {
              scheduler_configs: [
                {
                  scheduler_config_id: occupancyConfig.scheduler_config_id,
                  blocked_mask: blockedMask,
                  area_id: area.area_id,
                },
              ],
            },
          }),
        successMessage: willBeCapped
          ? t('calendar.areas_view.partial_capped', { count: blockedBits?.length ?? 0, area: areaLabel })
          : t('calendar.areas_view.area_closed', { area: areaLabel }),
      });
      await onBlocked();
      onClose();
    } catch {
      // safeExecute already surfaced the failure; keep the dialog open so the admin can retry.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ paddingBottom: 1 }}>
        <Grid container alignItems="center" gap={1} wrap="nowrap">
          <Grid
            sx={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: swatchColor, flexShrink: 0 }}
          />
          <Typography component="span" sx={{ fontSize: '18px', fontWeight: 700 }}>
            {t('calendar.areas_view.close_area_titled', { area: areaLabel })}
          </Typography>
        </Grid>
        <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
          {configLabel ? `${configLabel} · ` : ''}
          {area.total} {unitWord}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container flexDirection="column" wrap="nowrap" gap={2}>
          {areaBlocks.length > 0 && (
            <Grid container flexDirection="column" wrap="nowrap" gap={0.5}>
              <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>
                {t('calendar.areas_view.existing_blocks')}
              </Typography>
              {areaBlocks.map((block) => (
                <Grid key={block.id} container alignItems="center" gap={1} wrap="nowrap">
                  <Typography dir="ltr" sx={{ fontSize: '12px', minWidth: '96px', color: 'text.secondary' }}>
                    {blockTimeLabel(block)}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', flex: 1, minWidth: 0 }}>{block.description}</Typography>
                  <Button size="small" onClick={() => removeBlock(block)}>
                    {isSimpleAreaBlock(block, area)
                      ? t('calendar.areas_view.remove_block')
                      : t('calendar.areas_view.edit_block')}
                  </Button>
                </Grid>
              ))}
              <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                {t('calendar.areas_view.complex_blocks_explanation')}
              </Typography>
              <Divider sx={{ marginTop: 1 }} />
            </Grid>
          )}

          <FormControl>
            <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>{t('calendar.areas_view.what_to_close')}</Typography>
            <RadioGroup value={scope} onChange={(event) => setScope(event.target.value as BlockScope)}>
              <FormControlLabel
                value={BlockScope.WHOLE_AREA}
                control={<Radio size="small" />}
                label={t('calendar.areas_view.whole_area')}
              />
              <Grid container alignItems="center" gap={1} wrap="nowrap">
                <FormControlLabel
                  value={BlockScope.PARTIAL}
                  control={<Radio size="small" />}
                  label={t('calendar.areas_view.partial_area')}
                />
                <TextField
                  size="small"
                  type="number"
                  value={tableCount}
                  inputProps={{ min: 1, max: area.total, dir: 'ltr' }}
                  onFocus={() => setScope(BlockScope.PARTIAL)}
                  onChange={(event) => setTableCount(Number(event.target.value))}
                  sx={{ width: '90px' }}
                />
              </Grid>
            </RadioGroup>
            {scope === BlockScope.PARTIAL && (
              <Typography sx={{ fontSize: '12px', color: willBeCapped ? 'warning.main' : 'text.secondary' }}>
                {t('calendar.areas_view.free_right_now', { count: freeBits.length })}
              </Typography>
            )}
          </FormControl>

          <FormControl>
            <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>{t('calendar.areas_view.when')}</Typography>
            <RadioGroup value={when} onChange={(event) => setWhen(event.target.value as BlockWhen)}>
              <FormControlLabel
                value={BlockWhen.ALL_DAY}
                control={<Radio size="small" />}
                label={t('calendar.areas_view.all_day')}
              />
              <FormControlLabel
                value={BlockWhen.TIME_RANGE}
                control={<Radio size="small" />}
                label={t('calendar.areas_view.time_range')}
              />
            </RadioGroup>
            {/* A time range always reads start-then-end left to right, in both directions. */}
            <Grid
              container
              gap={1}
              alignItems="center"
              wrap="nowrap"
              sx={{ flexDirection: i18n.dir() === 'rtl' ? 'row-reverse' : 'row', justifyContent: 'flex-start' }}>
              <SharedMobileTimePicker
                value={fromSeconds}
                size="small"
                label={t('calendar.unavailability_dialog.start_time')}
                minutesStep={slotMinutes}
                minTime={asTime(dayStartSeconds)}
                maxTime={asTime(dayEndSeconds)}
                onChange={(value) => {
                  setWhen(BlockWhen.TIME_RANGE);
                  setFromSeconds(secondsOf(value));
                }}
              />
              <SharedMobileTimePicker
                value={toSeconds}
                size="small"
                label={t('calendar.unavailability_dialog.end_time')}
                minutesStep={slotMinutes}
                minTime={asTime(dayStartSeconds)}
                maxTime={asTime(dayEndSeconds)}
                onChange={(value) => {
                  setWhen(BlockWhen.TIME_RANGE);
                  setToSeconds(secondsOf(value));
                }}
              />
            </Grid>
            {isRangeInvalid && (
              <Typography sx={{ fontSize: '12px', color: 'error.main' }}>
                {t('calendar.areas_view.invalid_range')}
              </Typography>
            )}
          </FormControl>

          <TextField
            size="small"
            fullWidth
            label={t('calendar.areas_view.block_description')}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button variant="contained" disabled={isSubmitting || isRangeInvalid || hasNothingToBlock} onClick={submit}>
          {t('calendar.areas_view.close_area')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
